import React from 'react';

// Composant de rendu avec passages cliquables et meilleure mise en forme
const DisplaySynthesisRender = ({ content, className = '', documentRefs = [], onPassageClick }) => {
  // Fonction pour extraire les sections significatives du texte
  // afin de les rendre cliquables et les associer à des passages de documents
  const processContent = (text) => {
    if (!text) return '<p>Aucun contenu disponible</p>';

    // 1. Diviser d'abord le texte en paragraphes
    // Utiliser les doubles sauts de ligne comme séparateurs de paragraphes
    const originalParagraphs = text.split('\n\n').filter(Boolean);
    
    // 2. Traiter les références explicites (liens vers les documents)
    const paragraphsWithLinks = originalParagraphs.map(paragraph => {
      return paragraph.replace(
        /\[\[(\d+)\]\]\((.*?)\)/g,
        (match, num, url) => {
          return ` <a href="${url}" 
            data-doc-ref="true"
            class="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 bg-blue-50 text-blue-500 rounded-full text-xs hover:bg-blue-100 transition-colors">
            <span>${num}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-external-link"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </a>`;
        }
      );
    });
    
    // 3. Traiter chaque paragraphe pour les phrases cliquables 
    const processedParagraphs = paragraphsWithLinks.map(paragraph => {
      // Diviser le paragraphe en phrases
      const sentences = paragraph.split(/(?<=\.\s)/);
      
      // Rendre cliquable chaque phrase qui contient un numéro de page
      const processedSentences = sentences.map(sentence => {
        // Vérifier si la phrase contient un numéro de page entre parenthèses
        // Modifié pour chercher (page X) n'importe où dans la phrase
        const pageNumberMatch = sentence.match(/\(page\s*(\d+)\)/i);
        if (!pageNumberMatch || !pageNumberMatch[1]) {
          return sentence; // Pas de numéro de page, retourner telle quelle
        }
        
        // Extraire le numéro de page
        const pageNumber = parseInt(pageNumberMatch[1], 10);
        
        // Si aucun document disponible, retourner la phrase telle quelle
        if (!documentRefs || documentRefs.length === 0) {
          return sentence;
        }
        
        // Utiliser le premier document comme référence par défaut
        const docToUse = documentRefs[0];
        
        // Préparer un extrait du passage
        const bestPassage = Array.isArray(docToUse.extraits) && docToUse.extraits.length > 0
          ? (typeof docToUse.extraits[0] === 'string' 
             ? docToUse.extraits[0] 
             : docToUse.extraits[0]?.content || "")
          : "";
        
        // Rendre la phrase cliquable
        return `<span class="cursor-pointer transition-colors border-b border-blue-300 hover:bg-blue-100 rounded" 
                 data-clickable="true" 
                 data-doc-id="${docToUse.id}" 
                 data-page-number="${pageNumber}"
                 data-passage="${encodeURIComponent(bestPassage.substring(0, 150))}"
                 title="Cliquez pour voir la page ${pageNumber} du document">${sentence}</span>`;
      });
      
      // Reconstituer le paragraphe
      return processedSentences.join('');
    });
    
    // 4. Créer les paragraphes HTML avec un bon espacement
    return processedParagraphs.map((paragraph, index) => {
      return `<p class="mb-4 text-base leading-relaxed">${paragraph}</p>`;
    }).join('');
  };

  // Gestionnaire de clic pour les liens et les phrases cliquables
  const handleClick = (e) => {
    // Cas 1: Clic sur un lien de document explicite
    if (e.target.tagName === 'A' || e.target.closest('a')) {
      const link = e.target.tagName === 'A' ? e.target : e.target.closest('a');
      const href = link.getAttribute('href');
      
      if (href && link.getAttribute('data-doc-ref') && onPassageClick) {
        e.preventDefault();
        e.stopPropagation();
        
        // Trouver le document correspondant
        const docRef = documentRefs.find(doc => doc.id === href);
        if (docRef) {
          // Construire l'objet passage
          const passage = {
            content: Array.isArray(docRef.extraits) && docRef.extraits.length > 0
              ? (typeof docRef.extraits[0] === 'string' ? docRef.extraits[0] : docRef.extraits[0]?.content || "")
              : "",
            pageNumber: Array.isArray(docRef.relevantPages) && docRef.relevantPages.length > 0
              ? docRef.relevantPages[0] : 1
          };
          
          setTimeout(() => {
            onPassageClick(href, passage);
          }, 0);
        }
      }
      return;
    }
    
    // Cas 2: Clic sur une phrase cliquable
    const clickableSpan = e.target.closest('[data-clickable="true"]');
    if (clickableSpan && onPassageClick) {
      e.preventDefault();
      e.stopPropagation();
      
      const docId = clickableSpan.getAttribute('data-doc-id');
      const pageNumber = parseInt(clickableSpan.getAttribute('data-page-number')) || 1;
      const passageContent = decodeURIComponent(clickableSpan.getAttribute('data-passage') || "");
      
      // Créer l'objet passage
      const passage = {
        content: passageContent,
        pageNumber: pageNumber
      };
      
      setTimeout(() => {
        onPassageClick(docId, passage);
      }, 0);
    }
  };

  // Créer le HTML sécurisé
  const createMarkup = () => {
    return { __html: processContent(content) };
  };

  return (
    <div 
      className={`text-gray-800 ${className}`}
      dangerouslySetInnerHTML={createMarkup()} 
      onClick={handleClick}
    />
  );
};

export default DisplaySynthesisRender;